import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  RadioGroup,
  Select,
  TextareaAutosize,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { closeModal, openModal } from 'features/utilsSlice';
import { deleteRequest, getRequest, postRequest, putRequest } from 'utils/api';
import { formatPrice } from 'utils/helpers';
import useModalOpen from 'hooks/useModalOpen';
import ChevronDownIcon from 'public/icons/chevron-down.svg';
import BinIcon from 'public/icons/bin.svg';
import WishCardPreview from './WishCardPreview';
import TransitionDialog from 'components/TransitionDialog';
import BottomButtons from 'components/BottomButtons';
import RadioButton from 'components/RadioButton';
import ConfirmationDialog from 'components/ConfirmationDialog';
import styles from 'styles/components/Wishlist.module.scss';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    height: 40,
    minWidth: '100%',
    maxWidth: '100%',
    border: '1px solid rgb(200, 204, 217)',
    borderRadius: 20,
  },

  icon: {
    width: 13,
    height: 13,
    top: 'calc(70% - 12px)',
    right: 15,
  },

  paper: {
    borderRadius: 10,
    boxShadow: '0 0 30px 0 rgba(0, 0, 0, 0.1)',
  },

  menuList: {
    padding: '12px 10px',
  },

  menuListItem: {
    padding: '5px 5px',
    margin: '3px 0',
    fontFamily: 'Avenir Heavy',
    fontSize: 14,
    color: '#323643',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',

    '&.Mui-selected': {
      borderTopRightRadius: 5,
      borderTopLeftRadius: 5,
      backgroundColor: '#e8fefc',

      '&:hover': {
        backgroundColor: '#e8fefc',
        borderTopRightRadius: 5,
        borderTopLeftRadius: 5,
        color: '#323643',
      },
    },

    '&:hover': {
      backgroundColor: '#e8fefc',
      borderTopRightRadius: 5,
      borderTopLeftRadius: 5,
      color: '#323643',
    },
  },

  optionImage: {
    marginRight: 5,
    borderRadius: '50%',

    '& img': {
      borderRadius: '50%',
      objectFit: 'cover',
    },
  },
});

const MenuProps = {
  PaperProps: {
    style: {
      padding: '0 10px',
      borderRadius: 10,
      transition:
        'opacity 257ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 171ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      boxShadow: '0 0 30px 0 rgba(0, 0, 0, 0.1)',
    },
  },
  getContentAnchorEl: null,
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  autoFocus: false,
};

const width = { minWidth: 120, width: 120 };
const descriptionStyle = { fontSize: 12, opacity: 0.6 };

const WishFormDialog = ({ setActiveWishes }) => {
  const [wishlistOptions, setWishlistOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState({});
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [wish, setWish] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const modalWish = useSelector(state => state.utils.modalData['Wish Form']?.wish);

  const open = useModalOpen('Wish Form');
  const dispatch = useDispatch();
  const classes = useStyles();

  const hasError = wish.price && wish.price <= 5;
  const disabled = !wish.wishlist_option_id || !wish.price || hasError;

  const handleClose = () => {
    dispatch(closeModal('Wish Form'));
    setSelectedOption({});
    setWish({});
  };

  const handleSelect = event => {
    const option = wishlistOptions.find(option => option.id === event.target.value);

    setSelectedOption(option);
    setWish(prevWish => ({
      ...option,
      ...prevWish,
      id: prevWish.id,
      price: option.default_price,
      wishlist_option_id: option.id,
    }));
  };

  const handleChange = event => setWish(prevState => ({ ...prevState, kind: event.target.value }));

  const handleSubmit = () => {
    setIsLoading(true);

    let [request, endpoint, message] = wish.id
      ? [putRequest, `wishlist_items/${wish.id}`, 'Successfully updated a wish!']
      : [postRequest, 'wishlist_items', 'Successfully added a wish!'];

    const data = new FormData();

    Object.keys(wish).forEach(key => {
      if (wish[key] !== null && wish[key] !== undefined) {
        data.append(`wishlist_item[${key}]`, wish[key]);
      }
    });

    request({
      endpoint,
      data,
      config: { headers: { 'Content-Type': 'multipart/form-data' } },
      successMessage: message,
    })
      .then(response => {
        handleClose();

        if (wish.id) {
          setActiveWishes(prevState =>
            prevState.map(item => (item.id === response.data.id ? response.data : item))
          );
        } else {
          setActiveWishes(prevState => [...prevState, response.data]);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const removeWishItem = () => {
    deleteRequest({
      endpoint: `wishlist_items/${wish.id}`,
      successMessage: 'Successfully deleted a wish!',
    }).then(() => {
      setActiveWishes(prevState => prevState.filter(item => item.id !== wish.id));
      dispatch(closeModal('Confirmation'));
      setOpenConfirmation(false);
      handleClose();
    });
  };

  useEffect(() => {
    getRequest({
      endpoint: 'wishlist_options',
      query: { status: 'active' },
    })
      .then(options => setWishlistOptions(options))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!modalWish) return;

    setWish(modalWish);
    setSelectedOption(wishlistOptions.find(option => option.id === modalWish.wishlist_option_id));
  }, [modalWish, wishlistOptions]);

  return (
    <>
      <TransitionDialog open={open} handleClose={handleClose} styles={styles}>
        <div className={styles.container}>
          <div className={styles.header}>
            <h3>{wish.id ? 'Update wish' : 'Add a wish'}</h3>

            {wish.id && (
              <IconButton
                onClick={() => {
                  dispatch(openModal({ name: 'Confirmation', data: { action: 'approve' } }));
                  setOpenConfirmation(true);
                }}
                style={{ position: 'absolute', right: 5, display: 'flex' }}
              >
                <BinIcon />
              </IconButton>
            )}
          </div>

          <div className={styles.content}>
            <InputLabel>Wish option</InputLabel>

            <Select
              value={selectedOption.id || ''}
              onChange={handleSelect}
              className={classes.root}
              MenuProps={MenuProps}
              IconComponent={ChevronDownIcon}
              displayEmpty={true}
              inputProps={{
                placeholder: 'Select a wish',
                classes: {
                  icon: classes.icon,
                },
              }}
              disableUnderline
              renderValue={selected => {
                if (selectedOption.id) return selectedOption.name;

                if (selected) {
                  return wishlistOptions
                    .filter(item => item.id === selected)
                    .map(item => item.name);
                } else {
                  return 'Choose a wish option';
                }
              }}
              input={
                <Input
                  style={
                    selectedOption.id
                      ? { color: '#323643', padding: '0 20px' }
                      : { color: 'rgba(50, 54, 67, 0.4)', padding: '0 20px' }
                  }
                />
              }
            >
              {wishlistOptions.map((option, index) => (
                <MenuItem
                  key={index}
                  value={option.id}
                  className={classes.menuListItem}
                  style={index === wishlistOptions.length - 1 ? { borderBottom: 'none' } : {}}
                >
                  <div className={classes.optionImage}>
                    <Image
                      src={option?.image_url}
                      width={50}
                      height={50}
                      alt={option.name}
                      unoptimized
                    />
                  </div>

                  <span style={{ fontSize: 16 }}>{option.name}</span>

                  <span style={{ display: 'block', marginLeft: 'auto' }}>
                    {formatPrice(option.default_price)}
                  </span>
                </MenuItem>
              ))}
            </Select>

            {selectedOption.id && (
              <div className={styles.form}>
                <InputLabel>Price</InputLabel>

                <TextField
                  type="number"
                  defaultValue={wish.price}
                  onChange={e =>
                    setWish(prevState => ({
                      ...prevState,
                      price: +e.target.value,
                    }))
                  }
                  variant="outlined"
                  placeholder="Change price"
                  error={hasError}
                  helperText={hasError ? 'must be greater than 5.' : ''}
                />

                <InputLabel>Description</InputLabel>

                <TextareaAutosize
                  value={wish.description}
                  onChange={e =>
                    setWish(prevState => ({ ...prevState, description: e.target.value }))
                  }
                  minRows={4}
                  maxRows={4}
                  placeholder={
                    wish.id ? 'Give some details on this product' : selectedOption.description
                  }
                />

                <InputLabel style={{ marginTop: 20 }}>Product URL (optional)</InputLabel>

                <TextField
                  type="string"
                  defaultValue={wish.url}
                  onChange={e =>
                    setWish(prevState => ({
                      ...prevState,
                      url: e.target.value,
                    }))
                  }
                  variant="outlined"
                  placeholder="Paste a link to the product"
                />

                <RadioGroup name="wish-kind" value={wish.kind} onChange={handleChange}>
                  <div style={{ marginBottom: 10 }}>
                    <FormControlLabel
                      value="single_wish"
                      style={width}
                      control={<RadioButton />}
                      label="Single Wish"
                    />

                    <FormControlLabel
                      value="crowdfunding"
                      control={<RadioButton />}
                      label="Crowdfunding"
                      style={width}
                    />
                  </div>
                </RadioGroup>

                <WishCardPreview wish={wish} setWish={setWish} />

                {wish.kind === 'single_wish' && (
                  <div style={{ marginTop: 20 }}>
                    <FormControlLabel
                      control={<Checkbox color="primary" />}
                      label="Allow Repeat Purchase"
                      checked={wish.allow_repeat_purchase}
                      onChange={e =>
                        setWish(prevState => ({
                          ...prevState,
                          allow_repeat_purchase: e.target.checked,
                        }))
                      }
                    />

                    <small style={descriptionStyle}>
                      Make sure to mark if you'd like this gift to be reordered. If not, the item
                      will be removed from your wishlist after the initial purchase.
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>

          <BottomButtons
            handleClose={handleSubmit}
            isLoading={isLoading}
            disabled={disabled}
            text={wish.id ? 'Update' : 'Add'}
            outlined
            fixed
          />
        </div>
      </TransitionDialog>

      {openConfirmation && (
        <ConfirmationDialog title="Are you sure?" handleApprove={removeWishItem} />
      )}
    </>
  );
};

export default WishFormDialog;
